import React, { useCallback } from 'react';
import { InstagramCreator, PostInsights } from '@frontend/gql/social/graphql';
import { ResponsiveGrid, GridItemWrapper, GridSkeleton, GridEmptyState } from '@/shadcn/components/grid-layout';
import { SectionContainer, SectionHeader } from '@/shadcn/components/section';
import { SimilarCreatorsContentPreviewCard } from './similar-creators-content-preview-card';

interface SimilarCreatorsSectionProps {
  similarCreators: InstagramCreator[] | null;
  onCreatorClick: (creator: InstagramCreator) => void;
  isFetching?: boolean;
}

// Transform InstagramCreator to PostInsights format for ContentPreviewCard
const transformCreatorToPostInsights = (creator: InstagramCreator): PostInsights => ({
    __typename: 'PostInsights',
    entityId: creator.id,
    memberId: creator.id,
    accountEntityId: creator.id,
    accountUsername: creator.username,
    entityNetwork: 'INSTAGRAM',
    entityType: 'POST',
    mentionId: creator.id,
    postDate: new Date().toISOString(), // Use current date since we don't have a specific post date
    postUpdateDate: new Date().toISOString(),
    postCaption: creator.biography || `@${creator.username}`, // Use bio as caption, fallback to username
    postImageUrl: creator.profilePictureUrl || '',
    postPermalink: `https://www.instagram.com/${creator.username}`,
    postExternalId: creator.id,
    advertisableMediaPermissions: null,
    member: {
      __typename: 'SocialMember',
      id: creator.id,
      name: creator.name || creator.username,
      profilePicture: creator.profilePictureUrl,
      instagramUsername: creator.username,
      pinterestUsername: null,
      tiktokUsername: null,
      youtubeUsername: null,
    },
    total: {
      __typename: 'Insights',
      impressions: creator.insights?.total?.followers || 0, // Use followers as "impressions"
      engagements: creator.insights?.recent?.engagement || creator.insights?.recent?.reach || 0, // Use recent engagement, fallback to reach
      engagementRate: 0,
      tmv: 0,
    },
  });

export const SimilarCreatorsSection: React.FC<SimilarCreatorsSectionProps> = ({
  similarCreators,
  onCreatorClick,
  isFetching = false,
}) => {
  const isEmpty = !similarCreators || similarCreators.length === 0;

  const handleOpenExternal = useCallback(
    (creator: InstagramCreator) => () => {
      window.open(`https://www.instagram.com/${creator.username}`, '_blank', 'noopener,noreferrer');
    },
    []
  );

  return (
    <SectionContainer>
      <SectionHeader title="Similar Creators" />

      {isFetching ? (
        <GridSkeleton
          count={6}
          className="grid-cols-2 md:grid-cols-3"
        />
      ) : isEmpty ? (
        <GridEmptyState message="No similar creators found." />
      ) : (
        <ResponsiveGrid className="grid-cols-2 md:grid-cols-3">
          {similarCreators?.map((creator) => (
            <GridItemWrapper
              key={creator.id}
              item={creator}
              onItemClick={onCreatorClick}
              ariaLabel={`View details for ${creator.username || 'creator'}`}
            >
              <SimilarCreatorsContentPreviewCard
                post={transformCreatorToPostInsights(creator)}
                onOpenExternal={handleOpenExternal(creator)}
                showDownload={false}
              />
            </GridItemWrapper>
          ))}
        </ResponsiveGrid>
      )}
    </SectionContainer>
  );
};
